import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { registerQuery, useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H1, H3 } from 'UIComponents/elements/headings';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import Carousel from 'marketplace-ui-common/components/Carousel'; // @ts-expect-error untyped import

import ProfileListing from 'ecosystem-marketplace-core/records/ProfileListing';
import * as FilterKey from '../../../constants/search/FilterKey';
import { searchProfiles } from '../../../clients/profilesClient';
import { fromFilterStateToLocation, fromFilterStateToQuery } from '../../../utils/storefrontFilters';
import { getServices } from '../../../slices/serviceCatalogSlice'; // @ts-expect-error untyped import

import ResultCard from '../../results/ResultCard'; // @ts-expect-error untyped import

import { getUserFirstName } from '../../../selectors/auth'; // @ts-expect-error untyped import

import { translateFilterValue } from '../../../utils/i18n'; // @ts-expect-error untyped import

import { push } from '../../../utils/history';
import FindPartnerWizard from './FindPartnerWizard';
const GET_PROFILES = registerQuery({
  args: ['filters', 'limit'],
  fieldName: 'profiles',
  fetcher: ({
    filters,
    limit
  }) => {
    return searchProfiles(Object.assign({}, fromFilterStateToQuery(filters), {
      limit
    }));
  }
});
export default function FindPartnerWizardResultsCarousel({
  isMobile
}) {
  const firstName = useSelector(getUserFirstName);
  const catalogServices = useSelector(getServices);
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const {
    cardsPerPage
  } = useResponsiveProps({
    cardsPerPage: {
      lg: 3,
      md: 2,
      xs: 1
    }
  });
  const filters = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('QUESTIONNAIRE_FILTERS'));
    } catch (error) {
      console.error(error);
      return {};
    }
  }, []);
  const {
    data: profilesData
  } = useQuery(GET_PROFILES, {
    variables: {
      filters,
      limit: 15
    }
  });
  const handleViewAllClick = useCallback(() => {
    const {
      pathname,
      search
    } = fromFilterStateToLocation(filters, catalogServices, true);
    push({
      pathname,
      search: `?ref=questionnaire${search}`
    });
  }, [catalogServices, filters]);
  const renderedFilterString = useMemo(() => {
    let filterString = '';
    filters[FilterKey.SERVICE].forEach(serviceId => {
      const service = catalogServices.find(s => s.id === serviceId);

      if (service && service.name) {
        filterString = filterString.concat(service.name).concat(', ');
      }
    });
    filters[FilterKey.LANGUAGE].forEach(language => {
      filterString = filterString.concat(translateFilterValue(FilterKey.LANGUAGE, language)).concat(', ');
    });

    if (filters[FilterKey.COUNTRY]) {
      filterString = filterString.concat(filters[FilterKey.COUNTRY]).concat(', ');
    }

    return filterString.slice(0, filterString.length - 2);
  }, [catalogServices, filters]);
  const renderedProfileCards = useMemo(() => {
    if (!profilesData) {
      return [];
    }

    return profilesData.profiles[0].items.map(profile => {
      return /*#__PURE__*/_jsx("span", {
        className: isMobile ? 'm-right-5' : "",
        children: /*#__PURE__*/_jsx(ResultCard, {
          result: ProfileListing.from(profile)
        })
      }, profile.id);
    });
  }, [isMobile, profilesData]);
  const renderedNewProjectCard = useMemo(() => {
    return /*#__PURE__*/_jsx(UICardWrapper, {
      className: "p-y-10 m-top-5" + (!isMobile ? " p-x-10" : ""),
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        className: isMobile ? 'p-x-6' : 'p-x-10',
        direction: isMobile ? 'column' : 'row',
        justify: "between",
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          children: [/*#__PURE__*/_jsx(H3, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.homepage.newProject.title"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.homepage.newProject.subtitle"
          })]
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: () => setShowFindPartnerWizard(true),
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.homepage.newProject.button"
          })
        })]
      })
    });
  }, [isMobile, setShowFindPartnerWizard]);
  const renderedResults = useMemo(() => {
    if (isMobile) {
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UIScrollContainer, {
          children: /*#__PURE__*/_jsx(UIFlex, {
            align: "stretch",
            direction: "row",
            children: renderedProfileCards
          })
        }), renderedNewProjectCard]
      });
    }

    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-3",
      children: [/*#__PURE__*/_jsx(Carousel, {
        maxCardsPerPage: cardsPerPage,
        showPagination: false,
        cards: renderedProfileCards
      }), renderedNewProjectCard]
    });
  }, [cardsPerPage, isMobile, renderedNewProjectCard, renderedProfileCards]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(H1, {
      children: firstName ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.resultsCarousel.welcomeName",
        options: {
          name: firstName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.resultsCarousel.welcome"
      })
    }), /*#__PURE__*/_jsx(H3, {
      className: "m-bottom-2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystem-marketplace-solutions-ui.homepage.resultsCarousel.subtitle"
      })
    }), /*#__PURE__*/_jsxs(UIFlex, {
      direction: isMobile ? 'column' : 'row',
      justify: "between",
      children: [/*#__PURE__*/_jsxs("span", {
        children: [renderedFilterString, /*#__PURE__*/_jsx(UIButton, {
          className: !isMobile ? 'm-left-1' : "",
          onClick: () => setShowFindPartnerWizard(true),
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystem-marketplace-solutions-ui.storefront.results.header.updateQuestionnaire"
          })
        })]
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: handleViewAllClick,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.homepage.resultsCarousel.viewAll"
        })
      })]
    }), renderedResults, showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
      onClose: () => setShowFindPartnerWizard(false),
      isMobileView: isMobile
    })]
  });
}